import { Controller } from "@hotwired/stimulus"

let listeningForBfCache = false

function ensureBillingBfCacheReload() {
  if (listeningForBfCache || typeof window === "undefined") return
  listeningForBfCache = true
  window.addEventListener("pageshow", (event) => {
    if (!event.persisted) return
    if (!window.location.pathname.startsWith("/billing")) return
    window.location.reload()
  })
}

function exemptBillingPageFromTurboCache() {
  window.Turbo?.cache?.exemptPageFromCache?.()
}

// billings pages — data-controller="billing" on .billing-container / .billing-details-container
export default class extends Controller {
  #checkoutHandlers = null
  #connected = false

  connect() {
    this.#connected = true
    ensureBillingBfCacheReload()
    this.#initPopovers()
    this.#initVatSwitch()
    this.#initCardSwitcher()
    this.#initStripeCheckout()
    this.#initModals()
    this.#initPricingTabs()
  }

  disconnect() {
    this.#connected = false
    if (this.switchery) {
      this.switchery.destroy()
      this.switchery = null
    }
    $(this.element).find("[data-disabled-option], [data-feature-info]").popover("destroy")
    $(this.element).off(".billing")
    this.#checkoutHandlers?.forEach(({ el, handler }) => el.removeEventListener("click", handler))
    this.#checkoutHandlers = null
  }

  hideBlockChangePlanModal(event) {
    event.preventDefault()
    $(this.element).find(".js-block-change-plan-modal").modal("hide")
  }

  selectPricingTab(event) {
    const tabId = $(event.currentTarget).data("tab")

    if (tabId === 1) {
      $(".qb-main-pricing").removeClass("hide").addClass("active")
      $(".ai-assistant").removeClass("active").addClass("hide")
    } else {
      $(".qb-main-pricing").removeClass("active").addClass("hide")
      $(".ai-assistant").removeClass("hide").addClass("active")
    }

    $(".pricing-tab-content button").removeClass("active")
    $(event.currentTarget).addClass("active")
  }

  #initPopovers() {
    $(this.element)
      .find("[data-disabled-option]")
      .popover()
      .on("show.bs.popover", function () {
        $(this).data("bs.popover").tip().css("max-width", "600px")
      })

    $(this.element)
      .find("[data-feature-info]")
      .popover()
      .on("show.bs.popover", function () {
        $(this).data("bs.popover").tip().css("max-width", "600px")
      })
  }

  #initVatSwitch() {
    const vatCheckbox = this.element.querySelector(".js-switch")
    if (!vatCheckbox) return

    this.switchery = new Switchery(vatCheckbox, {
      size: "small",
      secondaryColor: "#D9E3F7",
      color: "#3978FC",
    })

    vatCheckbox.addEventListener("change", () => {
      $.ajax({
        url: window.page_args.update_eu_company_path,
        type: "POST",
        dataType: "JSON",
        data: { eu_company: vatCheckbox.checked },
        success: (response) => this.#showStripeError(response),
      })
    })
  }

  #initCardSwitcher() {
    const cards = this.element.querySelectorAll(".js-card")
    const showCards = this.element.querySelectorAll(".js-show-card")

    cards.forEach((card) => {
      card.addEventListener("click", () => {
        const cardId = card.dataset.cardId
        cards.forEach((c) => c.classList.remove("active"))
        card.classList.add("active")

        showCards.forEach((showCard) => showCard.classList.remove("active"))
        const target = this.element.querySelector(`.js-card-${cardId}`)
        if (target) target.classList.add("active")
      })
    })
  }

  #initStripeCheckout() {
    this.#checkoutHandlers = []

    const addCard = this.element.querySelector(".js-add-card")
    const promo = this.element.querySelector(".js-promo-paying")
    const paying = this.element.querySelector(".js-paying")
    const renew = this.element.querySelector(".js-renew-plan")

    if (addCard) this.#bindCheckoutButtons(addCard.dataset.stripeKey, "checkoutSession")
    if (promo) this.#bindPromoCheckout(promo.dataset.stripeKey)
    if (paying) this.#bindCheckoutButtons(paying.dataset.stripeKey, "planSession")
    if (renew) this.#bindCheckoutButtons(renew.dataset.stripeKey, "planSession", ".js-renew-checkout-button")
  }

  #bindCheckoutButtons(stripeKey, sessionAttr, buttonSelector = ".js-checkout-button") {
    this.element.querySelectorAll(buttonSelector).forEach((button) => {
      const handler = async (event) => {
        event.preventDefault()
        exemptBillingPageFromTurboCache()

        const stripe = await this.#stripe(stripeKey)
        if (!stripe || !this.#connected) return

        stripe
          .redirectToCheckout({ sessionId: button.dataset[sessionAttr] })
          .then((result) => this.#showStripeError(result))
      }
      button.addEventListener("click", handler)
      this.#checkoutHandlers.push({ el: button, handler })
    })
  }

  #bindPromoCheckout(stripeKey) {
    this.element.querySelectorAll(".js-checkout-promo-button").forEach((button) => {
      const originalText = button.textContent
      const handler = async (event) => {
        event.preventDefault()
        button.removeEventListener("click", handler)
        button.classList.add("disabled")
        button.textContent = "Processing..."

        if (button.classList.contains("no_session_id")) {
          exemptBillingPageFromTurboCache()
          window.location.href = window.page_args.paying_with_discount_path
        } else {
          exemptBillingPageFromTurboCache()
          const stripe = await this.#stripe(stripeKey)
          if (!stripe || !this.#connected) {
            button.addEventListener("click", handler)
            button.classList.remove("disabled")
            button.textContent = originalText
            return
          }

          $.ajax({
            url: window.page_args.get_session_path,
            success: (data) => {
              stripe
                .redirectToCheckout({ sessionId: data.session_id })
                .then((result) => this.#showStripeError(result))
            },
            error: (data) => {
              this.#showStripeError(data.responseJSON)
            },
          })
        }
      }

      button.addEventListener("click", handler)
      this.#checkoutHandlers.push({ el: button, handler })
    })
  }

  async #stripe(stripeKey) {
    if (!stripeKey) return null

    if (typeof window.Stripe !== "function") {
      await this.#loadStripeJs()
    }

    if (typeof window.Stripe !== "function") {
      this.#showStripeError({ error: { message: "Stripe checkout is unavailable. Please refresh the page and try again." } })
      return null
    }

    return window.Stripe(stripeKey, {})
  }

  #loadStripeJs() {
    const existingScript = document.querySelector('script[src^="https://js.stripe.com/v3"]')
    if (existingScript?.dataset.loaded === "true") return Promise.resolve()

    return new Promise((resolve) => {
      const script = existingScript || document.createElement("script")
      const timeout = window.setTimeout(resolve, 5000)

      script.addEventListener("load", () => {
        window.clearTimeout(timeout)
        script.dataset.loaded = "true"
        resolve()
      }, { once: true })
      script.addEventListener("error", () => {
        window.clearTimeout(timeout)
        resolve()
      }, { once: true })

      if (!existingScript) {
        script.src = "https://js.stripe.com/v3/"
        document.head.appendChild(script)
      }
    })
  }

  #initModals() {
    const euModal = this.element.querySelector(".js-set-eu-company-modal")
    if (euModal && euModal.dataset.show === "true") {
      $(euModal).modal("show")
    }

    const blockModal = this.element.querySelector(".js-block-change-plan-modal")
    if (blockModal) {
      if (blockModal.dataset.show === "true") {
        $(blockModal).modal("show")
      }
      const btn = blockModal.querySelector(".new-btn")
      if (btn) {
        btn.addEventListener("click", (e) => this.hideBlockChangePlanModal(e))
      }
    }
  }

  #initPricingTabs() {
    $(this.element)
      .find(".pricing-tab-content button")
      .on("click.billing", (e) => this.selectPricingTab(e))
  }

  #showStripeError(event) {
    const errorElement = this.element.querySelector(".error") || document.querySelector(".error")
    if (!errorElement) return

    errorElement.classList.remove("visible")

    if (event && event.error) {
      errorElement.textContent = event.error.message
      errorElement.classList.add("visible")
    }
  }
}
